/**
 * **小地图的触摸手势** —— 从 `Playground.vue` 抽出来（125 行）。
 *
 * ## 为什么必须自己写
 *
 * `@vue-flow/minimap` 的 `pannable` / `zoomable` **只认鼠标事件**
 * （`panHandler` 要求 `sourceEvent.type === 'mousemove'`，
 * `zoomHandler` 要求 `'wheel'`）。触摸时 d3-zoom 收下了事件、
 * 却什么也不做 —— 于是「小地图在手机上完全点不动」。
 *
 * 数学与推导在 `graph/minimapGesture.ts`（纯函数），这里只做
 * 「DOM 事实 → 纯函数 → store.setViewport」这一段接线。
 *
 * ## ⚠️ 抽出来的方式：函数体逐字不动
 *
 * 除了把 `graphEls` / `flowStores` 从闭包改成入参，函数体一字未改。
 * 这两件事都是「谁操作了哪个 Area」的时效性问题（见下面
 * `onMiniMapPointerDown` 那条注释），改写风险高、收益低。
 *
 * ## 归属
 *
 * 两个 Map（`flowStores` / `graphEls`）**不归这里** —— 它们是
 * 「Area → 图引擎实例 / DOM 根」的注册表，小地图只是其中一个消费者。
 * 见 `useGraphRegistries.ts` 的说明（那里记了我把归属放错一次的经过）。
 */
import { computed } from 'vue'
import type { ComputedRef, Ref } from 'vue'
import type { FlowStoreApi } from '@/graph/flowStore'
import { viewportOf } from '@/graph/flowStore'
import { FLOW_DOM } from '@/graph/graphDom'
import {
  distanceBetween,
  flowAtMiniMapPoint,
  midpointOf,
  panByMiniMapDrag,
  pinchAround,
} from '@/graph/minimapGesture'

export interface MiniMapMetrics {
  viewBox: { x: number; y: number; width: number; height: number }
  elementWidth: number
  elementHeight: number
}

/**
 * ⚠️ 四个入参都是**函数**（getter），不是 ref 本身。
 *
 * 原因：`boxSelectMode` / `spaceHeld` / `touchDevice` 在宿主里的**声明位置
 * 比这个 composable 晚**（`touchDevice` 尤其晚 —— 它来自 `useHotkeys`，
 * 而 `panOnDrag` 在模板里很早就用到了）。
 * 直接传 ref 会撞 TDZ；传 getter 就在**读取那一刻**才求值，
 * 这正是这个仓库处理「声明顺序」的老规矩（见 `useSocketTip`）。
 */
export interface MiniMapGestureCtx {
  /** 当前焦点 Area（`useAreas` 提供） */
  focusedId: Ref<string>
  /** 把某个 Area 切成焦点（`useAreas` 提供） */
  focusAreaById: (id: string) => void
  /** 框选模式：此时单指留给框选，不做平移 */
  /** 空格按住 = 临时平移 */
  /** 触摸设备（`useHotkeys` 提供，声明更晚） */
  touchDevice: () => boolean
  /** Area id → 画布 DOM 根（见 `useGraphRegistries`） */
  graphEls: Map<string, HTMLElement>
  /** Area id → 图引擎 store（同上） */
  flowStores: Map<string, FlowStoreApi>
}

export function useMiniMapGesture(ctx: MiniMapGestureCtx) {
  const { focusedId, focusAreaById, graphEls, flowStores, touchDevice } = ctx

  /** 从 DOM 读小地图的几何事实（不缓存：缩放/节点变化后 viewBox 会变） */
  function miniMapMetrics(areaId: string): MiniMapMetrics | null {
    /*
     * ⚠️ 选择器从 `graphDom.ts` 取（那是"画布必须提供的 DOM"那份契约），
     * **并且自己拼那个点** —— 契约表里存的是**class 名**（`np-graph__minimap`），
     * 不是选择器。
     *
     * ⚠️ 这里踩过一次，而且是"小地图完全点不动"的真凶：改契约名的时候
     * 把值从 `.vue-flow__minimap svg` 变成了 `np-graph__minimap`，
     * 而这一行是**直接把常量当选择器**用的 —— 于是 `querySelector('np-graph__minimap')`
     * 抛 `DOMException: not a valid selector`，每一次 pointerdown 都在
     * 这一行炸掉，用户看到的就是"拖不动"（还没有任何提示）。
     * 现在统一规矩：**表里只有 class 名，选择器由调用点拼**。
     */
    const svg = graphEls.get(areaId)?.querySelector(`.${FLOW_DOM.minimapSvg}`) as SVGSVGElement | null
    if (!svg) return null
    const vb = svg.viewBox?.baseVal
    const rect = svg.getBoundingClientRect()
    if (!vb || !vb.width || !rect.width) return null
    return {
      viewBox: { x: vb.x, y: vb.y, width: vb.width, height: vb.height },
      elementWidth: rect.width,
      elementHeight: rect.height,
    }
  }

  /** 正在按在小地图上的触点：id → 相对 svg 左上角的像素位置 */
  const miniMapPointers = new Map<number, { x: number; y: number }>()
  /** 上一次双指之间的距离 —— pinch 的 factor 靠它算 */
  let miniMapPinchStart = 0

  function miniMapLocal(ev: PointerEvent, svg: SVGSVGElement): { x: number; y: number } {
    const rect = svg.getBoundingClientRect()
    return { x: ev.clientX - rect.left, y: ev.clientY - rect.top }
  }

  function currentMiniMapSvg(areaId: string): SVGSVGElement | null {
    /* ⚠️ 同样自己拼点：契约表里是 class 名（见 `miniMapMetrics` 的注释） */
    return (
      (graphEls.get(areaId)?.querySelector(`.${FLOW_DOM.minimapSvg}`) as SVGSVGElement) ?? null
    )
  }

  function onMiniMapPointerDown(areaId: string, ev: PointerEvent) {
    /*
     * ⚠️ 先把焦点切到这个 Area。
     *
     * 小地图可能属于**非焦点**的那个窗口（比如上面是节点图、下面是另一个
     * 节点图，用户点的是下面那个）。手势必须打给**它自己**那一份 store，
     * 否则就是「拖了 A 的小地图，动的是 B 的画布」—— 正是用户反馈的
     * 「后创建的节点图窗口抢了之前的东西」。
     *
     * 顺带切焦点也符合 Blender：操作哪个 Area，它就成为激活的，
     * 底部状态栏跟着它走。
     */
    if (focusedId.value !== areaId) focusAreaById(areaId)

    const svg = currentMiniMapSvg(areaId)
    if (!svg) return
    /*
     * ⚠️ 2027：这里原来有一句 `if (ev.pointerType === 'mouse') return` ——
     * 「只接管触摸，鼠标交给 Vue Flow 自己的 pannable/zoomable 实现」。
     * 画布换成我们自己的之后**那套实现不存在了**，于是鼠标在小地图上
     * 完全没反应（真机反馈：「小地图还是无法操作」）。
     *
     * 现在这一份是**唯一**的实现，鼠标和触摸走同一条路：
     *   单指/左键拖 → 平移；双指 → 绕中点缩放（见 onMiniMapPointerMove）。
     * 之所以敢去掉那个分叉：以前是"两套实现会互相打架"，
     * 现在只有一套 —— 分叉的存在理由随之消失。
     *
     * ⚠️ 指针捕获由小地图组件负责（`NpCanvasMiniMap`）：不捕获的话，
     * 指针一离开小地图就收不到 move 了 —— 而拖动时地图内容在动，
     * 指针**很容易**滑出去，表现就是"拖两下就断"。
     */
    ev.stopPropagation()
    miniMapPointers.set(ev.pointerId, miniMapLocal(ev, svg))
    if (miniMapPointers.size === 2) {
      const [a, b] = [...miniMapPointers.values()]
      miniMapPinchStart = distanceBetween(a!, b!)
    }
  }

  function onMiniMapPointerMove(areaId: string, ev: PointerEvent) {
    const prev = miniMapPointers.get(ev.pointerId)
    if (prev === undefined) return
    /*
     * ⚠️ store 按**这个 Area** 取，不用 `flowStore`（那是焦点的）。
     * 拖动过程中焦点虽然已经切过来了，但显式按 id 取更不依赖时序。
     */
    const store = flowStores.get(areaId)
    if (!store?.setViewport) return
    const svg = currentMiniMapSvg(areaId)
    if (!svg) return
    const metrics = miniMapMetrics(areaId)
    if (!metrics) return

    const next = miniMapLocal(ev, svg)
    const vp = viewportOf(store)

    if (miniMapPointers.size === 1) {
      /* 单指：平移。位移取**相对上一次**的增量 */
      const panned = panByMiniMapDrag(vp, metrics, next.x - prev.x, next.y - prev.y)
      store.setViewport(panned)
    } else if (miniMapPointers.size === 2) {
      /* 双指：绕两指中点缩放 */
      miniMapPointers.set(ev.pointerId, next)
      const [a, b] = [...miniMapPointers.values()]
      const dist = distanceBetween(a!, b!)
      if (miniMapPinchStart > 0 && dist > 0) {
        const mid = midpointOf(a!, b!)
        const anchor = flowAtMiniMapPoint(metrics, mid.x, mid.y)
        store.setViewport(pinchAround(vp, dist / miniMapPinchStart, anchor))
        miniMapPinchStart = dist
      }
      return
    } else {
      return
    }
    miniMapPointers.set(ev.pointerId, next)
  }

  function onMiniMapPointerUp(ev: PointerEvent) {
    if (!miniMapPointers.has(ev.pointerId)) return
    miniMapPointers.delete(ev.pointerId)
    if (miniMapPointers.size < 2) miniMapPinchStart = 0
  }

  /*
   * ⚠️ 2027：`panOnDragForPointer` **删掉了**，连同它上面那一大段推导。
   *
   * 它存在的原因是**引擎的两个 prop 互相推导**：
   *
   * ```js
   * shouldSelectOnDrag = selectionKeyCode === true && shouldPanOnDrag !== true
   * ```
   *
   * 于是"左键框选、中键平移、触摸端别走 d3-zoom"这件事要靠在
   * `false` / `[1]` / `true` 三个值之间挑一个刚好不打架的 —— 注释里
   * 记着当时是怎么被源码钉死的（以及 TouchEvent 没有 `.button` 那个坑）。
   *
   * 现在画布是我们自己的，那三件事**是三条独立的规则**，不再互相推导：
   *
   * ```text
   *   左键拖空白处   → 框选（默认，任何设备一致）
   *   中键拖 / 空格拖 → 平移
   *   触摸双指        → 交给这里的 canvasPinch（平移 + 缩放）
   * ```
   *
   * 也就是说：这个文件里除了小地图手势，只剩"双指"那条 —— 而它本来
   * 就是宿主自己的 DOM 监听器（当年写它正是因为引擎给不了"单指框选 +
   * 双指缩放"这个组合）。
   */

  return {
    onMiniMapPointerDown,
    onMiniMapPointerMove,
    onMiniMapPointerUp,
  }
}
